// src/features/feedback/hooks/useFeedbackManagement.js
import { useState, useCallback } from 'react';
import { feedbackService } from '../services/feedbackService';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import i18n from '../../../i18n';

export function useFeedbackManagement() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const { showSuccess, showError } = useSnackbar();

  /**
   * Fetch feedback statistics
   */
  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await feedbackService.getFeedbackStatistics();
      setStatistics(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch feedback statistics';
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Update feedback visibility
   */
  const updateVisibility = useCallback(async (feedbackId, visibility) => {
    const t = i18n.t.bind(i18n);
    setUpdating(true);
    setError(null);

    try {
      const response = await feedbackService.updateFeedbackVisibility(
        feedbackId,
        visibility
      );
      showSuccess(t('feedback:messages.visibility_update_success'));
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || t('feedback:messages.visibility_update_error');
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [showSuccess, showError]);

  /**
   * Update feedback status and admin response
   */
  const updateStatus = useCallback(async (feedbackId, data) => {
    const t = i18n.t.bind(i18n);
    setUpdating(true);
    setError(null);

    try {
      const response = await feedbackService.updateFeedbackStatus(feedbackId, data);
      showSuccess(t('feedback:messages.status_update_success'));
      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || t('feedback:messages.status_update_error');
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [showSuccess, showError]);

  /**
   * Delete feedback
   */
  const deleteFeedback = useCallback(async (feedbackId) => {
    const t = i18n.t.bind(i18n);
    setDeleting(true);
    setError(null);

    try {
      const response = await feedbackService.deleteFeedback(feedbackId);
      showSuccess(t('feedback:messages.delete_success'));
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.message || t('feedback:messages.delete_error');
      setError(errorMessage);
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setDeleting(false);
    }
  }, [showSuccess, showError]);

  /**
   * Publish feedback (shortcut for updating visibility)
   */
  const publishFeedback = useCallback(async (feedbackId) => {
    return updateVisibility(feedbackId, 'PUBLISHED');
  }, [updateVisibility]);

  /**
   * Hide feedback (shortcut for updating visibility)
   */
  const hideFeedback = useCallback(async (feedbackId) => {
    return updateVisibility(feedbackId, 'HIDDEN');
  }, [updateVisibility]);

  /**
   * Mark as implemented (shortcut for updating status)
   */
  const markAsImplemented = useCallback(async (feedbackId, adminResponse = null) => {
    return updateStatus(feedbackId, {
      status: 'IMPLEMENTED',
      admin_response: adminResponse
    });
  }, [updateStatus]);

  /**
   * Mark as will not implement (shortcut for updating status)
   */
  const markAsWillNotImplement = useCallback(async (feedbackId, adminResponse = null) => {
    return updateStatus(feedbackId, {
      status: 'WILL_NOT_IMPLEMENT',
      admin_response: adminResponse
    });
  }, [updateStatus]);

  /**
   * Mark as pending review (shortcut for updating status)
   */
  const markAsPendingReview = useCallback(async (feedbackId, adminResponse = null) => {
    return updateStatus(feedbackId, {
      status: 'PENDING_REVIEW',
      admin_response: adminResponse
    });
  }, [updateStatus]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    statistics,
    loading,
    updating,
    deleting,
    error,

    // Actions
    fetchStatistics,
    updateVisibility,
    updateStatus,
    deleteFeedback,

    // Shortcuts
    publishFeedback,
    hideFeedback,
    markAsImplemented,
    markAsWillNotImplement,
    markAsPendingReview,

    // Utils
    clearError,
  };
}

/**
 * Hook for feedback status management with enums
 */
export function useFeedbackStatus() {
  const t = i18n.t.bind(i18n);
  const VISIBILITY_STATUS = {
    NOT_DECIDED: 'FeedbackVisibility.NOT_DECIDED',
    PUBLISHED: 'FeedbackVisibility.PUBLISHED',
    HIDDEN: 'FeedbackVisibility.HIDDEN',
  };

  const IMPLEMENTATION_STATUS = {
    NOT_DECIDED: 'FeedbackStatus.NOT_DECIDED',
    WILL_NOT_IMPLEMENT: 'FeedbackStatus.WILL_NOT_IMPLEMENT',
    PENDING_REVIEW: 'FeedbackStatus.PENDING_REVIEW',
    IMPLEMENTED: 'FeedbackStatus.IMPLEMENTED',
  };

  const getVisibilityLabel = useCallback((status) => {
    const labels = {
      [VISIBILITY_STATUS.NOT_DECIDED]: t("feedback:status.visibility.not_decided", 'Not Decided'),
      [VISIBILITY_STATUS.PUBLISHED]: t("feedback:status.visibility.published", 'Published'),
      [VISIBILITY_STATUS.HIDDEN]: t("feedback:status.visibility.hidden", 'Hidden'),
    };
    return labels[status] || status;
  }, [VISIBILITY_STATUS]);

  const getImplementationLabel = useCallback((status) => {
    const labels = {
      [IMPLEMENTATION_STATUS.NOT_DECIDED]: t("feedback:status.implementation.not_decided", 'Not Decided'),
      [IMPLEMENTATION_STATUS.WILL_NOT_IMPLEMENT]: t("feedback:status.implementation.will_not_implement", 'Will Not Implement'),
      [IMPLEMENTATION_STATUS.PENDING_REVIEW]: t("feedback:status.implementation.pending_review", 'Pending Review'),
      [IMPLEMENTATION_STATUS.IMPLEMENTED]: t("feedback:status.implementation.implemented", 'Implemented'),
    };
    return labels[status] || status;
  }, [IMPLEMENTATION_STATUS]);

  const getVisibilityColor = useCallback((status) => {
    const colors = {
      'FeedbackVisibility.NOT_DECIDED': 'default',
      'FeedbackVisibility.PUBLISHED': 'success',
      'FeedbackVisibility.HIDDEN': 'error',
      'NOT_DECIDED': 'default',
      'PUBLISHED': 'success',
      'HIDDEN': 'error',
    };
    return colors[status] || 'default';
  }, []);

  const getImplementationColor = useCallback((status) => {
    const colors = {
      'FeedbackStatus.NOT_DECIDED': 'default',
      'FeedbackStatus.WILL_NOT_IMPLEMENT': 'error',
      'FeedbackStatus.PENDING_REVIEW': 'warning',
      'FeedbackStatus.IMPLEMENTED': 'success',
      'NOT_DECIDED': 'default',
      'WILL_NOT_IMPLEMENT': 'error',
      'PENDING_REVIEW': 'warning',
      'IMPLEMENTED': 'success',
    };
    return colors[status] || 'default';
  }, []);

  return {
    VISIBILITY_STATUS,
    IMPLEMENTATION_STATUS,
    getVisibilityLabel,
    getImplementationLabel,
    getVisibilityColor,
    getImplementationColor,
  };
}